import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SearchIcon } from '../components/Icons';
import merchantService from '../services/merchantService';
import './MerchantSearch.css';

const PAGE_SIZE = 10;
const EXAMPLES = ['Kingmidas', 'Malaysia', 'MYR'];

const MatchList = ({ matches }) => (
  <dl className="merchant-search-matches">
    {matches.map((match, index) => (
      <div key={`${match.label}-${index}`}>
        <dt>{match.label}</dt>
        <dd>{match.value}</dd>
      </div>
    ))}
  </dl>
);

const MerchantResult = ({ merchant, canViewMerchant }) => {
  const matches = merchant.search_matches || [];
  return (
    <article className="merchant-search-result">
      <div className="merchant-search-result-heading">
        <div>
          {canViewMerchant ? (
            <Link className="merchant-search-merchant-name" to={`/merchants/${merchant._id}/view`}>{merchant.name}</Link>
          ) : (
            <span className="merchant-search-merchant-name">{merchant.name}</span>
          )}
          <p className="merchant-search-codes">
            <code>{merchant.merchant_code}</code>
            {merchant.client_code && <span>Client: <code>{merchant.client_code}</code></span>}
          </p>
        </div>
        <span className={`merchant-search-status ${merchant.is_active ? 'is-active' : ''}`}>
          {merchant.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <MatchList matches={matches.slice(0, 3)} />
      {matches.length > 3 && (
        <details className="merchant-search-more-matches">
          <summary>{matches.length - 3} more matching details</summary>
          <MatchList matches={matches.slice(3)} />
        </details>
      )}
      {canViewMerchant && <Link className="merchant-search-view-link" to={`/merchants/${merchant._id}/view`}>View merchant →</Link>}
    </article>
  );
};

const MerchantSearch = () => {
  const { hasPermission, getDefaultRoute } = useAuth();
  const canView = hasPermission('merchant_search', 'view');
  const canViewMerchant = hasPermission('merchants', 'view');
  const [draft, setDraft] = useState('');
  const [turns, setTurns] = useState([]);
  const [loadingTurnId, setLoadingTurnId] = useState(null);
  const inputRef = useRef(null);
  const conversationRef = useRef(null);
  const turnIdRef = useRef(0);
  const sessionRef = useRef(0);
  const requestRef = useRef(null);
  const isLoading = loadingTurnId !== null;

  useEffect(() => () => {
    sessionRef.current += 1;
    requestRef.current?.abort();
  }, []);

  useEffect(() => {
    const conversation = conversationRef.current;
    if (conversation) conversation.scrollTop = conversation.scrollHeight;
  }, [turns.length]);

  const updateTurn = (id, update) => {
    setTurns(previous => previous.map(turn => turn.id === id ? update(turn) : turn));
  };

  const fetchResults = async (id, search, page = 1) => {
    if (!canView || requestRef.current) return;
    const controller = new AbortController();
    const session = sessionRef.current;
    requestRef.current = controller;
    setLoadingTurnId(id);
    updateTurn(id, turn => ({ ...turn, error: '', status: page === 1 ? 'loading' : 'ready' }));

    try {
      const result = await merchantService.searchMerchants({ search, page, limit: PAGE_SIZE }, { signal: controller.signal });
      if (session !== sessionRef.current) return;
      if (!result.success) throw new Error(result.message || 'Unable to search merchants. Please try again.');
      updateTurn(id, turn => {
        const existingIds = new Set(turn.merchants.map(merchant => merchant._id));
        const incoming = result.data || [];
        return {
          ...turn,
          merchants: page === 1 ? incoming : [...turn.merchants, ...incoming.filter(merchant => !existingIds.has(merchant._id))],
          pagination: result.pagination,
          status: 'ready',
          error: ''
        };
      });
    } catch (error) {
      if (session !== sessionRef.current) return;
      updateTurn(id, turn => ({ ...turn, status: turn.pagination ? 'ready' : 'error', error: error.message }));
    } finally {
      if (session === sessionRef.current) {
        requestRef.current = null;
        setLoadingTurnId(null);
      }
    }
  };

  const submitSearch = value => {
    const search = value.trim();
    if (!search || requestRef.current || !canView) return;
    const id = ++turnIdRef.current;
    setTurns(previous => [...previous, { id, search, merchants: [], pagination: null, status: 'loading', error: '' }]);
    setDraft('');
    fetchResults(id, search);
    inputRef.current?.focus();
  };

  const startNewSearch = () => {
    sessionRef.current += 1;
    requestRef.current?.abort();
    requestRef.current = null;
    setLoadingTurnId(null);
    setTurns([]);
    setDraft('');
    inputRef.current?.focus();
  };

  if (!canView) return <Navigate to={getDefaultRoute()} replace />;

  return (
    <div className="merchant-search-page card">
      <div className="merchant-search-toolbar">
        <div>
          <h1>Merchant Search</h1>
          <p>Find connections across all merchants.</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={startNewSearch} disabled={!turns.length && !draft}>New search</button>
      </div>

      <div ref={conversationRef} className="merchant-search-conversation" role="region" aria-label="Search conversation" tabIndex={0}>
        {turns.length === 0 ? (
          <div className="merchant-search-welcome">
            <span className="merchant-search-welcome-icon" aria-hidden="true"><SearchIcon /></span>
            <h2>What would you like to find?</h2>
            <p>Enter a provider name, merchant code, URL, or IP address. See which merchants match and how they are connected.</p>
            <div className="merchant-search-examples" aria-label="Example searches">
              {EXAMPLES.map(example => <button key={example} type="button" className="btn btn-outline" onClick={() => submitSearch(example)}>{example}</button>)}
            </div>
          </div>
        ) : turns.map(turn => (
          <section key={turn.id} className="merchant-search-turn" aria-label={`Search for ${turn.search}`}>
            <div className="merchant-search-user-message"><span>You</span><p>{turn.search}</p></div>
            <div className="merchant-search-reply">
              <div className="merchant-search-reply-label"><SearchIcon /><span>Merchant Search</span></div>
              {turn.status === 'loading' ? (
                <div className="merchant-search-loading" role="status">
                  <p>Searching all merchants for “{turn.search}”…</p>
                  <div className="merchant-search-skeleton" aria-hidden="true" />
                  <div className="merchant-search-skeleton" aria-hidden="true" />
                </div>
              ) : (
                <>
                  {turn.pagination && (
                    <p className="merchant-search-answer" role="status">
                      {turn.pagination.total > 0
                        ? <>Found <strong>{turn.pagination.total} {turn.pagination.total === 1 ? 'merchant' : 'merchants'}</strong> matching “{turn.search}”.</>
                        : <>No merchants match “{turn.search}”. Try a shorter name, provider code, URL, or IP address.</>}
                    </p>
                  )}
                  <div className="merchant-search-results">
                    {turn.merchants.map(merchant => <MerchantResult key={merchant._id} merchant={merchant} canViewMerchant={canViewMerchant} />)}
                  </div>
                  {turn.error && (
                    <div className="merchant-search-error" role="alert">
                      <p>{turn.error}</p>
                      <button type="button" className="btn btn-secondary" disabled={isLoading} onClick={() => fetchResults(turn.id, turn.search, turn.pagination ? turn.pagination.page + 1 : 1)}>Try again</button>
                    </div>
                  )}
                  {turn.pagination?.total > 0 && (
                    <div className="merchant-search-result-footer">
                      <span>Showing {turn.merchants.length} of {turn.pagination.total} merchants</span>
                      {turn.pagination.page < turn.pagination.pages && !turn.error && (
                        <button type="button" className="btn btn-secondary" disabled={isLoading} onClick={() => fetchResults(turn.id, turn.search, turn.pagination.page + 1)}>
                          {loadingTurnId === turn.id ? 'Loading…' : 'Load more'}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        ))}
      </div>

      <form className="merchant-search-composer" onSubmit={event => { event.preventDefault(); submitSearch(draft); }}>
        <label htmlFor="merchant-search-message">Search across all merchants</label>
        <div className="merchant-search-input-row">
          <textarea
            ref={inputRef}
            id="merchant-search-message"
            value={draft}
            rows={2}
            placeholder="Try Kingmidas, a merchant code, a URL, or an IP…"
            aria-describedby="merchant-search-hint"
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
                event.preventDefault();
                submitSearch(draft);
              }
            }}
          />
          <button type="submit" className="btn btn-primary" disabled={!draft.trim() || isLoading}><SearchIcon /><span>Search</span></button>
        </div>
        <p id="merchant-search-hint">Keyword search · Providers, payment gateways, sources, countries, currencies, URLs, IPs, and merchant details.</p>
      </form>
    </div>
  );
};

export default MerchantSearch;
